import React, { useEffect, useState } from 'react'
import { Button, DropdownMenu, Input, Select, TextArea, TextTooltip } from '@components'
import { ButtonProps } from '@components/Button'
import { InputGroup } from '@components/Inputs/InputGroup.tsx'
import styled, { css } from 'styled-components'
import { cloneDeep } from 'lodash'
import { SUPPORTED_LANGUAGES } from '@constants'
import { useTranslation } from 'react-i18next'
import { FaPlus, FaQuestionCircle, FaTimes } from 'react-icons/fa'
import { ValidLocale } from '@localisation'

export interface ITranslation {
  key: string
  value: string
}

interface Props extends React.DetailedHTMLProps<any, HTMLInputElement> {
  name: string
  info?: string
  label?: string
  value: ITranslation[]
  prioritizedLang?: ValidLocale
  multiline?: boolean
  languageSelector?: 'select' | 'pills'
  languageSelectorInfo?: React.ReactNode
  onActiveLanguageChange?: (key: string | undefined) => void
}

const BLANK_OPTION_KEY = 'blank'
const LANGUAGE_PRIORITY = [ValidLocale.EN, ValidLocale.DE, ValidLocale.FI, ValidLocale.SV]

export const TranslatedInput: React.FC<Props> = ({
  label,
  info,
  value,
  onChange,
  prioritizedLang,
  multiline = false,
  languageSelector = 'select',
  languageSelectorInfo,
  onActiveLanguageChange
}) => {
  const { t } = useTranslation()

  // Modify language priority such way that prioritizedLang is always first
  const languagePriority = [
    ...(prioritizedLang ? [prioritizedLang] : []),
    ...LANGUAGE_PRIORITY.filter(lang => lang !== prioritizedLang)
  ]

  const languages = [...SUPPORTED_LANGUAGES].sort((a, b) => {
    const langA = languagePriority.indexOf(a.value)
    const langB = languagePriority.indexOf(b.value)
    return langA - langB
  })

  const sortedValue = [...(value || [])].sort((a, b) => {
    const langA = languagePriority.indexOf(a.key as ValidLocale)
    const langB = languagePriority.indexOf(b.key as ValidLocale)
    return langA - langB
  })

  const [activeKey, setActiveKey] = useState(sortedValue[0]?.key)

  useEffect(() => {
    if (!value?.some(field => field.key === activeKey)) {
      setActiveKey(value?.[0]?.key)
    }
  }, [activeKey, value])

  useEffect(() => {
    onActiveLanguageChange?.(activeKey)
  }, [activeKey, onActiveLanguageChange])

  const remainingLanguages = languages.filter(
    ({ value: languageValue }) => !value?.some(field => field.key === languageValue)
  )

  const nextLanguageKey = () => remainingLanguages[0]?.value || BLANK_OPTION_KEY

  const handleAddLanguage = (lang: string) => {
    if (!lang || lang === BLANK_OPTION_KEY || checkIfDisabled(lang)) {
      return
    }
    onChange?.([
      ...cloneDeep(value || []),
      {
        key: lang,
        value: ''
      }
    ])
    setActiveKey(lang)
  }

  const handleAddNew = () => handleAddLanguage(nextLanguageKey())
  const handleValueChange = (lang: string, translation: string) =>
    onChange?.(
      cloneDeep(value).map(field => {
        if (field.key === lang) {
          field.value = translation
        }
        return field
      })
    )
  const handleLangChange = (oldLang: string, newLang: string) =>
    onChange?.(
      cloneDeep(value).map(field => {
        if (field.key === oldLang) {
          field.key = newLang
        }
        return field
      })
    )
  const handleOnDelete = (lang: string) =>
    onChange?.(cloneDeep(value).filter(field => field.key !== lang))

  const checkIfDisabled = (lang: string) => value?.map(({ key }) => key).includes(lang)

  const removalDisabled = value?.length <= 1
  const additionDisabled = value?.length >= languages.length
  const Field = multiline ? TextArea : Input
  const activeField = sortedValue.find(field => field.key === activeKey) || sortedValue[0]

  if (languageSelector === 'pills') {
    const addTranslationTrigger = (
      <AddLangPill type="button" $muted>
        + {t('components.translatedInput.addTranslation')}
      </AddLangPill>
    ) as React.ReactElement<ButtonProps>

    return (
      <StyledTranslationInput>
        <LanguagePills>
          {sortedValue.map(field => {
            const language = languages.find(({ value }) => value === field.key)
            return (
              <LangPill key={field.key} $active={field.key === activeField?.key}>
                <LangPillLabel type="button" onClick={() => setActiveKey(field.key)}>
                  {language ? t(language.labelKey) : field.key}
                </LangPillLabel>
                {!removalDisabled && (
                  <RemoveLang
                    type="button"
                    aria-label={t('components.translatedInput.removeTranslation')}
                    onClick={() => handleOnDelete(field.key)}
                  >
                    <FaTimes />
                  </RemoveLang>
                )}
              </LangPill>
            )
          })}
          {!additionDisabled && (
            <DropdownMenu
              placement="bottom-start"
              items={remainingLanguages.map(({ value: languageValue, labelKey }) => (
                <Button
                  key={languageValue}
                  type="button"
                  isSecondary
                  onClick={() => handleAddLanguage(languageValue)}
                >
                  {t(labelKey)}
                </Button>
              ))}
            >
              {addTranslationTrigger}
            </DropdownMenu>
          )}
          {languageSelectorInfo && (
            <LanguageSelectorInfo>
              <TextTooltip tooltip={languageSelectorInfo}>
                <FaQuestionCircle />
              </TextTooltip>
            </LanguageSelectorInfo>
          )}
        </LanguagePills>
        {activeField && (
          <Field
            label={label}
            info={info}
            value={activeField.value}
            onChange={e => handleValueChange(activeField.key, e.target.value)}
          />
        )}
      </StyledTranslationInput>
    )
  }

  return (
    <StyledTranslationInput>
      {sortedValue.map((field, i) => (
        <InputGroup key={i} $sizes={[5, 2]}>
          <Field
            label={label}
            info={info}
            value={field.value}
            onChange={e => handleValueChange(field.key, e.target.value)}
          />
          <Select
            label={t('components.translatedInput.language')}
            value={field.key}
            onChange={e => handleLangChange(field.key, e.target.value)}
          >
            <option key={BLANK_OPTION_KEY} value="" />
            {languages.map(({ value: languageValue, labelKey }) => (
              <option
                key={languageValue}
                value={languageValue}
                disabled={checkIfDisabled(languageValue)}
              >
                {t(labelKey)}
              </option>
            ))}
          </Select>

          <Button
            size="sm"
            type="button"
            icon={<FaTimes />}
            isSecondary={true}
            onClick={() => handleOnDelete(field.key)}
            disabled={removalDisabled}
          />
        </InputGroup>
      ))}

      {!additionDisabled && (
        <Footer>
          <Button type="button" icon={<FaPlus />} onClick={() => handleAddNew()} isSecondary={true}>
            {t('components.translatedInput.addTranslation')}
          </Button>
        </Footer>
      )}
    </StyledTranslationInput>
  )
}

const StyledTranslationInput = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md}rem;
  margin-bottom: ${({ theme }) => theme.spacing.sm}rem;

  & > label {
    padding: 0 ${({ theme }) => theme.spacing.xs}rem;
    color: ${({ theme }) => theme.colors.metalGray};
    font-weight: bold;
  }
`

const LanguagePills = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing.xs}rem;
`

const LanguageSelectorInfo = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  cursor: pointer;
`

const langPillColors = css<{ $active?: boolean; $muted?: boolean }>`
  background: ${({ theme, $active }) => ($active ? theme.colors.menuBg : theme.colors.neutralGray)};
  color: ${({ theme, $active, $muted }) =>
    $active
      ? theme.colors.neutralWhite
      : $muted
      ? theme.colors.iconGray
      : theme.dashboard.textSubtle};
`

const langPillLayout = css`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  font-weight: 600;
`

const LangPill = styled.div<{ $active?: boolean; $muted?: boolean }>`
  ${langPillLayout}
  ${langPillColors}
  overflow: hidden;
`

const AddLangPill = styled.button<{ $active?: boolean; $muted?: boolean }>`
  ${langPillLayout}
  ${langPillColors}
  padding: 2px 8px;
  cursor: pointer;
`

const LangPillLabel = styled.button`
  border: none;
  background: transparent;
  padding: 2px 8px;
  font: inherit;
  font-weight: inherit;
  color: inherit;
  cursor: pointer;
`

const RemoveLang = styled.button`
  display: inline-flex;
  align-items: center;
  align-self: stretch;
  border: none;
  background: transparent;
  padding: 2px 8px 2px 0;
  color: inherit;
  cursor: pointer;
  opacity: 0.7;
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  line-height: 1;

  &:hover {
    opacity: 1;
  }
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`
